import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Play, CheckCircle, Download } from 'lucide-react';
import api, { uploadRequestConfig } from '../../services/api';
import CommentThread from '../common/CommentThread';
import StatusBadge from '../common/StatusBadge';
import PriorityBadge from '../common/PriorityBadge';
import FileUploader from '../common/FileUploader';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [commentData, setCommentData] = useState({
    message: '',
    updateStatus: ''
  });
  const [files, setFiles] = useState([]);

  useEffect(() => {
    fetchTaskDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when task id changes
  }, [id]);

  const fetchTaskDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/employee/tasks/${id}`);
      setTask(response.data.task);
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast.error('Failed to fetch task details');
      navigate('/employee/tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!commentData.message.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append('message', commentData.message.trim());
      
      if (commentData.updateStatus) {
        formData.append('updateStatus', commentData.updateStatus);
      }

      files.forEach(file => {
        formData.append('attachments', file);
      });

      const response = await api.post(`/employee/tasks/${id}/comments`, formData, uploadRequestConfig());

      setTask(response.data.task);
      setCommentData({ message: '', updateStatus: '' });
      setFiles([]);
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error(error.response?.data?.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getAvailableStatusActions = () => {
    const actions = [];
    
    if (task.status === 'assigned') {
      actions.push({
        value: 'in-progress',
        label: 'Start Task',
        description: 'Mark this task as in progress',
        icon: Play,
        color: 'yellow'
      });
    }
    
    if (task.status === 'in-progress') {
      actions.push({
        value: 'review-pending',
        label: 'Submit for Review',
        description: 'Request admin to review your work',
        icon: CheckCircle,
        color: 'purple'
      });
    }

    return actions;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = task?.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';
  const availableActions = task ? getAvailableStatusActions() : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!task) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start space-x-4">
        <button
          onClick={() => navigate('/employee/tasks')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
        >
          <ArrowLeft className="h-6 w-6 text-gray-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{task.title}</h1>
            <StatusBadge status={task.status} reviewDecision={task.reviewDecision} />
            <PriorityBadge priority={task.priority} />
          </div>
          {isOverdue && (
            <div className="inline-flex items-center px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-2">
              Overdue
            </div>
          )}
          <div className="text-sm text-gray-600 space-y-1">
            <p>Assigned by: <span className="font-medium">{task.assignedBy?.name}</span></p>
            <p>Assigned on: {formatDate(task.createdAt)}</p>
            {task.dueDate && (
              <p>Due: <span className={isOverdue ? 'text-red-600 font-medium' : ''}>{formatDate(task.dueDate)}</span></p>
            )}
            {task.completedAt && (
              <p className="text-green-600 font-medium">Completed on: {formatDate(task.completedAt)}</p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Task Details & Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Task Description */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Task Description</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 whitespace-pre-wrap">{task.description}</p>

              {/* Initial Attachments */}
              {task.initialAttachments && task.initialAttachments.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Attached Files:</h4>
                  <div className="space-y-2">
                    {task.initialAttachments.map((file, index) => (
                      <a
                        key={index}
                        href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/uploads/tasks/${file.filename}`}
                        download={file.originalName}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 text-sm group"
                      >
                        <Download className="h-4 w-4" />
                        <span className="group-hover:underline">{file.originalName}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Activity & Comments */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Activity & Comments</h3>
            </div>
            <div className="p-6">
              <CommentThread
                comments={task.comments}
                statusHistory={task.statusHistory}
                currentUser={user}
              />
            </div>
          </div>

          {/* Add Comment Form (only if not completed) */}
          {task.status !== 'completed' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Add Update</h3>
              </div>
              <form onSubmit={handleCommentSubmit} className="p-6 space-y-4">
                {/* Comment Textarea */}
                <div>
                  <textarea
                    value={commentData.message}
                    onChange={(e) => setCommentData({ ...commentData, message: e.target.value })}
                    placeholder="Share your progress, ask questions, or provide updates..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>

                {/* File Upload */}
                <FileUploader
                  files={files}
                  onFilesChange={setFiles}
                  maxFiles={3}
                  label="Attach Files (Optional)"
                />

                {/* Status Actions */}
                {availableActions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Update Status (Optional)
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="statusUpdate"
                          value=""
                          checked={commentData.updateStatus === ''}
                          onChange={(e) => setCommentData({ ...commentData, updateStatus: e.target.value })}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">Keep current status</span>
                      </label>

                      {availableActions.map((action) => {
                        const Icon = action.icon;
                        return (
                          <label 
                            key={action.value}
                            className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="statusUpdate"
                              value={action.value}
                              checked={commentData.updateStatus === action.value}
                              onChange={(e) => setCommentData({ ...commentData, updateStatus: e.target.value })}
                              className="text-primary-600 focus:ring-primary-500"
                            />
                            <Icon className={`h-5 w-5 text-${action.color}-600`} />
                            <div>
                              <span className="text-sm font-medium text-gray-900">{action.label}</span>
                              <p className="text-xs text-gray-500">{action.description}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting || !commentData.message.trim()}
                    className="flex items-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Posting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Post Update
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Completed Message */}
          {task.status === 'completed' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <h4 className="font-medium text-green-900">Task Completed!</h4>
                  <p className="text-sm text-green-700 mt-1">
                    This task has been approved and marked as complete by the admin.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Co-assignees (if any) */}
        {task.assignedTo && task.assignedTo.length > 1 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Team ({task.assignedTo.length})
                </h3>
              </div>
              <div className="p-6 space-y-3">
                {task.assignedTo.map((employee) => (
                  <div
                    key={employee._id}
                    className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold ${
                      employee._id === user?.id ? 'bg-primary-600' : 'bg-gray-500'
                    }`}>
                      {employee.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {employee.name}
                        {employee._id === user?.id && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-primary-100 text-primary-700 rounded">
                            You
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {employee.email}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDetail;
